import { Add, Edit } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { getImportBranches } from 'controllers'
import { PortOfEntryDto } from 'dtos'
import { SortDirection } from 'localConstants/sortDirection'
import { enqueueSnackbar } from 'notistack'
import { PortOfEntryParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useDebounce } from 'utils'
import PortOfEntryMaintenanceAddEditDialog from './PortOfEntryMaintenanceAddEditDialog'

export default function PortOfEntryMaintenanceListCard() {
  const [isGettingPortOfEntry, setIsGettingPortOfEntry] = useState<boolean>(false)
  const [isPortOfEntryMaintenanceSaving, setIsPortOfEntryMaintenanceSaving] = useState<boolean>(false)
  const [portOfEntry, setPortOfEntry] = useState<PortOfEntryDto>(new PortOfEntryDto())
  const [PortOfEntryMaintenanceAddEditDialogOpen, setPortOfEntryMaintenanceAddEditDialogOpen] = useState<boolean>(false)
  const [portsOfEntry, setPortsOfEntry] = useState<PortOfEntryDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [parameters, setParameters] = useState<{
    page: number
    pageSize: number
    orderBy: string
    order: SortDirection
  }>({
    page: 0,
    pageSize: 10,
    orderBy: 'portOfEntry',
    order: 'desc'
  })

  useEffect(() => {
    getPortsOfEntry(parameters)
  }, [parameters])

  const getPortsOfEntry = useDebounce((parameters: PortOfEntryParameters) => {
    setIsGettingPortOfEntry(true)
    controllers
      .getPortOfEntry(parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setPortsOfEntry(response.value)
      })
      .finally(() => {
        setIsGettingPortOfEntry(false)
      })
  }, 100)

  return (
    <>
      <PortOfEntryMaintenanceAddEditDialog
        initialValues={portOfEntry}
        isLoading={isPortOfEntryMaintenanceSaving}
        onClose={() => {
          setPortOfEntryMaintenanceAddEditDialogOpen(false)
        }}
        onSave={(values: PortOfEntryDto) => {
          const requestMethod = values.portOfEntryGuid ? controllers.updatePortOfEntry : controllers.createPortOfEntry

          setIsPortOfEntryMaintenanceSaving(true)
          requestMethod(values)
            .then(() => {
              setPortOfEntryMaintenanceAddEditDialogOpen(false)
              enqueueSnackbar('Port Of Entry Saved Successfully!', { variant: 'success' })
              getPortsOfEntry(parameters)
              getImportBranches()
            })
            .finally(() => {
              setIsPortOfEntryMaintenanceSaving(false)
            })
        }}
        open={PortOfEntryMaintenanceAddEditDialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Ports of Entry Maintenance
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  onClick={() => {
                    setPortOfEntry(new PortOfEntryDto())
                    setPortOfEntryMaintenanceAddEditDialogOpen(true)
                  }}
                  variant='text'
                  endIcon={<Add />}
                >
                  ADD
                </Button>
              </Grid>
            </Grid>

            {isGettingPortOfEntry && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingPortOfEntry && (
              <Grid item xs={12}>
                <Card variant='outlined'>
                  <CardContent>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={theme => ({ color: theme.palette.primary.main })}
                              sortDirection={parameters.orderBy === 'cityState' ? parameters.order : false}
                            >
                              <TableSortLabel
                                active={parameters.orderBy === 'cityState'}
                                direction={parameters.orderBy === 'cityState' ? parameters.order : undefined}
                                onClick={() => {
                                  setParameters({
                                    ...parameters,
                                    order: parameters.orderBy === 'cityState' && parameters.order === 'asc' ? 'desc' : 'asc',
                                    orderBy: 'cityState'
                                  })
                                }}
                              >
                                Port Of Entry
                              </TableSortLabel>
                            </TableCell>

                            <TableCell
                              sx={theme => ({ color: theme.palette.primary.main })}
                              sortDirection={parameters.orderBy === 'portCode' ? parameters.order : false}
                            >
                              <TableSortLabel
                                active={parameters.orderBy === 'portCode'}
                                direction={parameters.orderBy === 'portCode' ? parameters.order : undefined}
                                onClick={() => {
                                  setParameters({
                                    ...parameters,
                                    order: parameters.orderBy === 'portCode' && parameters.order === 'asc' ? 'desc' : 'asc',
                                    orderBy: 'portCode'
                                  })
                                }}
                              >
                                Port Code
                              </TableSortLabel>
                            </TableCell>

                            <TableCell
                              sx={theme => ({ color: theme.palette.primary.main })}
                              sortDirection={parameters.orderBy === 'importBranches' ? parameters.order : false}
                            >
                              <TableSortLabel
                                active={parameters.orderBy === 'importBranches'}
                                direction={parameters.orderBy === 'importBranches' ? parameters.order : undefined}
                                onClick={() => {
                                  setParameters({
                                    ...parameters,
                                    order:
                                      parameters.orderBy === 'importBranches' && parameters.order === 'asc' ? 'desc' : 'asc',
                                    orderBy: 'importBranches'
                                  })
                                }}
                              >
                                Import Branches
                              </TableSortLabel>
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {portsOfEntry.map((portOfEntry, index) => (
                            <TableRow
                              key={portOfEntry.portOfEntryGuid}
                              sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}
                            >
                              <TableCell>{portOfEntry.cityState}</TableCell>

                              <TableCell>{portOfEntry.portCode}</TableCell>

                              <TableCell>
                                {portOfEntry.importBranches?.map(importBranch => importBranch.name).join(', ')}
                              </TableCell>

                              <TableCell padding='checkbox'>
                                <Button
                                  color='primary'
                                  endIcon={<Edit />}
                                  onClick={() => {
                                    setPortOfEntry(portOfEntry)
                                    setPortOfEntryMaintenanceAddEditDialogOpen(true)
                                  }}
                                  variant='text'
                                >
                                  EDIT
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>

                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={parameters.pageSize}
                              page={parameters.page}
                              onPageChange={(_, page) => {
                                setParameters({
                                  ...parameters,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setParameters({
                                  ...parameters,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                ||Click add to add a new record.|| Click edit to edit an existing record. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
